import { useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowLeft, Check, ShieldCheck, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/pages/LandingPage";
import { whatsAppLink, WhatsAppIcon } from "@/components/WhatsAppButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLANS = {
  address: { name: "Address Plan", monthly: 999 },
  gst: { name: "GST Plan", monthly: 1499 },
  business: { name: "Business Plan", monthly: 2499 },
};

const ADDONS = {
  gst_filing_1yr: { name: "1-Year GST Filing", price: 8999 },
  gst_filing_3m: { name: "3-Month GST Filing", price: 2500 },
  signage: { name: "Business Signage", price: 5000 },
  company_reg: { name: "Company Registration", price: 10000 },
};

const inr = (n) => `₹${n.toLocaleString("en-IN")}`;

export default function CheckoutPage() {
  const [params] = useSearchParams();
  const [plan, setPlan] = useState(params.get("plan") && PLANS[params.get("plan")] ? params.get("plan") : "gst");
  const [addons, setAddons] = useState(() => (params.get("addons") || "").split(",").filter((a) => ADDONS[a]));
  const [form, setForm] = useState({ customer_name: "", customer_email: "", customer_phone: "" });
  const [loading, setLoading] = useState(false);

  const yearly = PLANS[plan].monthly * 12;
  const addonTotal = addons.reduce((s, a) => s + ADDONS[a].price, 0);
  const total = yearly + addonTotal;

  const toggleAddon = (id) =>
    setAddons((list) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]));

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const pay = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/orders`, { plan, addons, ...form });
      const { load } = await import("@cashfreepayments/cashfree-js");
      const cashfree = await load({ mode: data.mode === "production" ? "production" : "sandbox" });
      await cashfree.checkout({ paymentSessionId: data.payment_session_id, redirectTarget: "_self" });
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 503) {
        toast.error("Online payments are being activated. Continue on WhatsApp and we'll set you up right away.");
      } else {
        toast.error(typeof d === "string" ? d : "Could not start payment. Please try again.");
      }
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-secondary/40 pb-20 md:pb-0" data-testid="checkout-page">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-black/5">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="checkout-logo-link"><Logo /></Link>
          <Link to="/" className="text-sm font-medium text-slate-600 hover:text-primary transition-colors duration-200 flex items-center gap-1.5" data-testid="checkout-back-link">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight" data-testid="checkout-headline">Checkout</h1>
          <p className="mt-2 text-slate-600">Yearly billing — your virtual office, sorted for the whole year.</p>
        </motion.div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Left: plan + addons + details */}
          <div className="lg:col-span-3 space-y-8">
            <section className="rounded-3xl bg-white border border-black/5 p-7 shadow-[0_2px_8px_rgb(0,0,0,0.04)]">
              <h2 className="font-display text-lg font-semibold text-slate-900 mb-4">1. Your plan (billed yearly)</h2>
              <div className="space-y-3" data-testid="checkout-plans">
                {Object.entries(PLANS).map(([id, p]) => (
                  <button type="button" key={id} onClick={() => setPlan(id)} data-testid={`checkout-plan-${id}`}
                    className={`w-full flex items-center justify-between rounded-2xl border-2 p-4 text-left transition-colors duration-200 ${
                      plan === id ? "border-primary bg-accent/50" : "border-slate-200 hover:border-primary/40"
                    }`}>
                    <div>
                      <p className="font-semibold text-slate-900">{p.name}</p>
                      <p className="text-xs text-muted-foreground">{inr(p.monthly)}/month × 12</p>
                    </div>
                    <p className="font-display text-lg font-bold text-slate-900">{inr(p.monthly * 12)}<span className="text-xs font-medium text-muted-foreground">/yr</span></p>
                  </button>
                ))}
              </div>
            </section>

            <section className="rounded-3xl bg-white border border-black/5 p-7 shadow-[0_2px_8px_rgb(0,0,0,0.04)]">
              <h2 className="font-display text-lg font-semibold text-slate-900 mb-4">2. Add-ons (one-time)</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="checkout-addons">
                {Object.entries(ADDONS).map(([id, a]) => (
                  <button type="button" key={id} onClick={() => toggleAddon(id)} data-testid={`checkout-addon-${id}`}
                    className={`flex items-start gap-3 rounded-2xl border-2 p-4 text-left transition-colors duration-200 ${
                      addons.includes(id) ? "border-primary bg-accent/50" : "border-slate-200 hover:border-primary/40"
                    }`}>
                    <span className={`mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 ${
                      addons.includes(id) ? "bg-primary border-primary" : "border-slate-300"
                    }`}>
                      {addons.includes(id) && <Check className="w-3.5 h-3.5 text-white" />}
                    </span>
                    <span>
                      <span className="block text-sm font-semibold text-slate-900">{a.name}</span>
                      <span className="block text-xs text-muted-foreground">+{inr(a.price)} one-time</span>
                    </span>
                  </button>
                ))}
              </div>
            </section>

            <section className="rounded-3xl bg-white border border-black/5 p-7 shadow-[0_2px_8px_rgb(0,0,0,0.04)]">
              <h2 className="font-display text-lg font-semibold text-slate-900 mb-4">3. Your details</h2>
              <form id="checkout-form" onSubmit={pay} className="space-y-4" data-testid="checkout-form">
                <div className="space-y-1.5">
                  <Label htmlFor="co-name">Full Name</Label>
                  <Input id="co-name" data-testid="checkout-name-input" className="h-12 rounded-xl" value={form.customer_name} onChange={set("customer_name")} required />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="co-email">Email</Label>
                    <Input id="co-email" type="email" data-testid="checkout-email-input" className="h-12 rounded-xl" value={form.customer_email} onChange={set("customer_email")} required />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="co-phone">Mobile</Label>
                    <Input id="co-phone" data-testid="checkout-phone-input" className="h-12 rounded-xl" value={form.customer_phone} onChange={set("customer_phone")} required />
                  </div>
                </div>
              </form>
            </section>
          </div>

          {/* Right: summary */}
          <div className="lg:col-span-2">
            <div className="lg:sticky lg:top-24 rounded-3xl bg-slate-900 text-white p-7 shadow-[0_20px_40px_rgb(0,0,0,0.15)]" data-testid="checkout-summary">
              <h2 className="font-display text-lg font-semibold">Order Summary</h2>
              <div className="mt-5 space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-white/70">{PLANS[plan].name} — yearly</span>
                  <span className="font-semibold" data-testid="summary-plan-price">{inr(yearly)}</span>
                </div>
                {addons.map((a) => (
                  <div key={a} className="flex justify-between">
                    <span className="text-white/70">{ADDONS[a].name}</span>
                    <span className="font-semibold">{inr(ADDONS[a].price)}</span>
                  </div>
                ))}
                <div className="border-t border-white/15 pt-3 flex justify-between items-end">
                  <span className="text-white/70">Total (one-time, yearly)</span>
                  <span className="font-display text-3xl font-bold text-primary" data-testid="summary-total">{inr(total)}</span>
                </div>
                <p className="text-xs text-white/50">GST extra as applicable. Billed once per year — no monthly charges.</p>
              </div>
              <Button type="submit" form="checkout-form" disabled={loading} data-testid="checkout-pay-button"
                className="mt-6 w-full h-14 rounded-full bg-primary hover:bg-primary/90 text-white text-base font-semibold shadow-lg shadow-primary/25 transition-transform duration-200 hover:-translate-y-0.5">
                {loading ? "Opening secure checkout..." : <><Lock className="mr-2 w-4 h-4" /> Pay {inr(total)}</>}
              </Button>
              <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-white/50">
                <ShieldCheck className="w-3.5 h-3.5" /> UPI · Cards · NetBanking — secured by Cashfree
              </p>
              <a href={whatsAppLink(`Hi Solve Desk! I want to purchase the ${PLANS[plan].name} (yearly)${addons.length ? ` with add-ons: ${addons.map((a) => ADDONS[a].name).join(", ")}` : ""}. Total: ${inr(total)}`)}
                target="_blank" rel="noopener noreferrer" data-testid="checkout-whatsapp-alt"
                className="mt-4 flex items-center justify-center gap-2 text-sm text-[#25D366] hover:underline">
                <WhatsAppIcon className="w-4 h-4" /> Prefer to pay over WhatsApp?
              </a>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
